import React from "react";
import { Link, useRoute } from "wouter";
import { SEOHead } from "@/components/SEOHead";
import { Header, Footer, Breadcrumbs } from "@/components/Navigation";
import { PRACTICE_INFO, ServiceItem } from "@/site-data";
import {
  Calendar,
  Phone,
  CheckCircle,
  HelpCircle,
  ArrowRight,
  ShieldCheck,
  Sparkles,
  MapPin
} from "lucide-react";

export default function ServiceDetail() {
  const [, params] = useRoute("/services/:slug");
  const slug = params?.slug;

  const service = PRACTICE_INFO.services.find((s) => s.slug === slug);

  if (!service) {
    return (
      <div className="min-h-screen flex flex-col bg-white">
        <Header />
        <div className="max-w-4xl mx-auto py-24 px-4 text-center space-y-4">
          <h1 className="font-heading text-3xl font-bold text-[#0f2b48]">Service Not Found</h1>
          <p className="text-slate-600">The requested treatment could not be found.</p>
          <Link href="/services/" className="inline-block bg-[#0284c7] text-white px-6 py-2.5 rounded-lg">
            View All Services
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const detailSchema = {
    "@context": "https://schema.org",
    "@type": "MedicalProcedure",
    name: service.title,
    description: service.fullDesc,
    procedureType: "Non-invasive or Surgical Podiatry",
    provider: {
      "@type": "Physician",
      name: PRACTICE_INFO.doctor,
      jobTitle: PRACTICE_INFO.doctorTitle,
      telephone: PRACTICE_INFO.phone,
      address: {
        "@type": "PostalAddress",
        streetAddress: PRACTICE_INFO.address.street,
        addressLocality: PRACTICE_INFO.address.city,
        addressRegion: PRACTICE_INFO.address.state,
        postalCode: PRACTICE_INFO.address.zip,
        addressCountry: "US"
      }
    }
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: service.faqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer
      }
    }))
  };

  const combinedSchema = {
    "@context": "https://schema.org",
    "@graph": [detailSchema, faqSchema]
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <SEOHead
        title={service.metaTitle}
        description={service.metaDesc}
        canonicalPath={`/services/${service.slug}/`}
        schema={combinedSchema}
      />
      <Header />
      <Breadcrumbs
        items={[
          { label: "Services", href: "/services/" },
          { label: service.title }
        ]}
      />

      <main className="flex-1">
        {/* HERO TITLE */}
        <section className="bg-[#0f2b48] text-white py-14 sm:py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-4">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-cyan-500/20 text-cyan-300 text-xs font-semibold tracking-wider uppercase border border-cyan-400/30">
              <ShieldCheck className="w-4 h-4" /> Wasatch Foot &amp; Ankle Speciality Care
            </span>
            <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-white max-w-4xl">
              {service.title}
            </h1>
            <p className="text-slate-300 text-base sm:text-lg max-w-3xl font-body leading-relaxed">
              {service.shortDesc}
            </p>
          </div>
        </section>

        {/* SERVICE DETAIL BODY */}
        <section className="py-14 sm:py-18 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
              {/* Main Clinical Copy */}
              <div className="lg:col-span-8 space-y-10">
                <div className="space-y-4 text-slate-700 font-body text-base leading-relaxed">
                  <h2 className="font-heading text-2xl sm:text-3xl font-bold text-[#0f2b48]">
                    Overview &amp; Clinical Approach
                  </h2>
                  <p>{service.fullDesc}</p>
                </div>

                {service.id === "swift-therapy" && (
                  <div className="rounded-3xl border border-slate-200 bg-slate-50 p-5 sm:p-7 space-y-4">
                    <div>
                      <span className="text-xs font-bold uppercase tracking-[0.16em] text-[#0284c7]">See how Swift works</span>
                      <h2 className="mt-2 font-heading text-2xl font-bold text-[#0f2b48]">A closer look at Swift microwave treatment</h2>
                      <p className="mt-2 text-sm leading-relaxed text-slate-600">Watch the educational video to learn more about the treatment approach. Dr. Harper will review whether it is appropriate for your plantar wart.</p>
                    </div>
                    <figure className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                      <img
                        src={PRACTICE_INFO.swiftDevicePhoto}
                        alt="Swift treatment probe and control unit"
                        className="w-full aspect-[4/3] object-contain bg-[#f4f8f8]"
                        loading="lazy"
                      />
                      <figcaption className="border-t border-slate-100 px-4 py-3 text-xs font-semibold text-slate-600">
                        Official Swift treatment equipment
                      </figcaption>
                    </figure>
                    <div className="aspect-video overflow-hidden rounded-2xl bg-slate-900 shadow-lg">
                      <iframe
                        title="Swift microwave wart treatment educational video"
                        src="https://www.youtube.com/embed/CxLnu-E53CI"
                        className="h-full w-full"
                        loading="lazy"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        allowFullScreen
                      />
                    </div>
                  </div>
                )}

                {/* Symptoms / Indications */}
                <div className="bg-slate-50 border border-slate-200 rounded-3xl p-6 sm:p-8 space-y-4">
                  <h3 className="font-heading text-xl font-bold text-[#0f2b48]">
                    Symptoms &amp; Clinical Signs To Watch For
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {service.symptoms.map((s, idx) => (
                      <div key={idx} className="flex items-start gap-2.5 text-sm text-slate-700">
                        <CheckCircle className="w-4 h-4 text-[#0284c7] shrink-0 mt-0.5" />
                        <span>{s}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Treatment Benefits */}
                <div className="space-y-4">
                  <h3 className="font-heading text-xl font-bold text-[#0f2b48]">
                    Treatment Plan &amp; Patient Benefits
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {service.treatments.map((t, idx) => (
                      <div
                        key={idx}
                        className="p-5 rounded-2xl bg-white border border-slate-200 shadow-sm flex items-start gap-3"
                      >
                        <Sparkles className="w-5 h-5 text-cyan-600 shrink-0 mt-0.5" />
                        <span className="text-sm font-medium text-slate-800">{t}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Frequently Asked Questions */}
                <div className="space-y-6 pt-4 border-t border-slate-200">
                  <div className="flex items-center gap-2">
                    <HelpCircle className="w-6 h-6 text-[#0284c7]" />
                    <h3 className="font-heading text-2xl font-bold text-[#0f2b48]">
                      Frequently Asked Questions
                    </h3>
                  </div>
                  <div className="space-y-4">
                    {service.faqs.map((faq, idx) => (
                      <div
                        key={idx}
                        className="bg-slate-50 rounded-2xl p-6 border border-slate-200/80 space-y-2"
                      >
                        <h4 className="font-heading font-bold text-base text-[#0f2b48]">
                          {faq.question}
                        </h4>
                        <p className="text-sm text-slate-600 font-body leading-relaxed">
                          {faq.answer}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Sticky Sidebar Booking & Info */}
              <div className="lg:col-span-4 space-y-6 sticky top-28">
                <div className="bg-[#0f2b48] text-white rounded-3xl p-6 sm:p-8 space-y-6 shadow-xl">
                  <div className="space-y-2">
                    <span className="text-cyan-300 text-xs font-bold uppercase tracking-wider">
                      Consultation Booking
                    </span>
                    <h3 className="font-heading text-2xl font-bold text-white">
                      Discuss This Treatment
                    </h3>
                    <p className="text-xs text-slate-300 font-body leading-relaxed">
                      Meet directly with Dr. Spence Harper in Heber City to evaluate your condition and determine the ideal treatment course.
                    </p>
                  </div>

                  <div className="space-y-3 pt-2">
                    <Link
                      href="/appointments/"
                      className="w-full inline-flex items-center justify-center gap-2 bg-[#0284c7] hover:bg-[#0369a1] text-white font-bold py-3.5 px-4 rounded-xl shadow transition-all"
                    >
                      <Calendar className="w-4 h-4" /> Request Appointment
                    </Link>
                    <a
                      href={`tel:${PRACTICE_INFO.phone}`}
                      className="w-full inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white font-semibold py-3.5 px-4 rounded-xl border border-white/20 transition-all text-sm"
                    >
                      <Phone className="w-4 h-4 text-cyan-300" /> Call: {PRACTICE_INFO.phone}
                    </a>
                  </div>

                  <div className="pt-4 border-t border-slate-700/80 space-y-2 text-xs text-slate-300">
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-cyan-400 shrink-0" />
                      <span>190 N. Main Street, Heber City, UT</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <ShieldCheck className="w-4 h-4 text-cyan-400 shrink-0" />
                      <span>Major Insurance &amp; Medicare Accepted</span>
                    </div>
                  </div>
                </div>

                {/* Other Services Navigation Widget */}
                <div className="bg-slate-50 border border-slate-200 rounded-2xl p-6 space-y-3">
                  <h4 className="font-heading font-bold text-sm text-[#0f2b48] uppercase tracking-wider">
                    Other Specialities
                  </h4>
                  <ul className="space-y-2 text-xs sm:text-sm">
                    {PRACTICE_INFO.services
                      .filter((s) => s.slug !== service.slug)
                      .map((s) => (
                        <li key={s.id}>
                          <Link
                            href={`/services/${s.slug}/`}
                            className="text-slate-700 hover:text-[#0284c7] flex items-center justify-between group"
                          >
                            <span>{s.title}</span>
                            <ArrowRight className="w-3.5 h-3.5 text-slate-400 group-hover:text-[#0284c7]" />
                          </Link>
                        </li>
                      ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
